const moment = require('moment');
const { sortPlanningEventsByMachineCategory } = require('./planningEventSort');
const { getPlanningManpower, getPlanningTargetOutput } = require('./planningCalendar');
const { countPlanningWorkingHours, formatPlanningStartDate, formatPlanningEndDate, getDayWorkWindow } = require('./planningSchedule');

const BOARD_SHIFT = Object.freeze({ startMinute: 0, durationMinutes: 24 * 60, slotMinutes: 60 });
const TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';

function getBoardWindow(date, shift = BOARD_SHIFT) {
    const start = moment(date).startOf('day').add(shift.startMinute, 'minutes');
    return { start, end: start.clone().add(shift.durationMinutes, 'minutes') };
}

function createBoardSlots(date, shift = BOARD_SHIFT) {
    const { start } = getBoardWindow(date, shift);
    return Array.from({ length: shift.durationMinutes / shift.slotMinutes }, (_, index) =>
        start.clone().add(index * shift.slotMinutes, 'minutes').format(TIMESTAMP_FORMAT));
}

function getBoardMachines(machines = [], events = [], category = '', selectedMachine = '') {
    const groups = new Map();
    machines.forEach(item => {
        const machine = String(item.machine || '').trim();
        if (machine && !groups.has(machine)) {
            groups.set(machine, { machine, description: item.description, events: [], assignment: { machineNo: machine, lineNo: '' } });
        }
    });
    events.forEach(event => {
        const machineNo = String(event.machineNo || '').trim();
        const lineNo = String(event.lineNo || '').trim();
        const machine = machineNo || lineNo;
        if (!machine) return;
        if (!groups.has(machine)) groups.set(machine, { machine, events: [], assignment: { machineNo, lineNo: machineNo ? '' : lineNo } });
        const group = groups.get(machine);
        if (group.events.length === 0) group.assignment = { machineNo, lineNo: machineNo ? '' : lineNo };
        group.events.push(event);
    });
    const matching = [...groups.values()].filter(group =>
        (!category || group.description === category) && (!selectedMachine || group.machine === selectedMachine));
    return sortPlanningEventsByMachineCategory(matching.map(board => ({ machineNo: board.machine, board })), machines)
        .map(item => item.board);
}

function clipBoardEvent(event, date, shift = BOARD_SHIFT) {
    const start = moment(event.start);
    const end = moment(event.end);
    const window = getBoardWindow(date, shift);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)
        || !start.isBefore(window.end) || !end.isAfter(window.start)) return null;
    const visibleStart = moment.max(start, window.start);
    const visibleEnd = moment.min(end, window.end);
    return {
        event,
        date,
        visibleStart: visibleStart.format(TIMESTAMP_FORMAT),
        startMinute: visibleStart.diff(window.start, 'minutes', true),
        endMinute: visibleEnd.diff(window.start, 'minutes', true),
        continuesBefore: start.isBefore(window.start),
        continuesAfter: end.isAfter(window.end),
    };
}

function layoutBoardDay(events = [], date, shift = BOARD_SHIFT) {
    const clipped = events.map(event => clipBoardEvent(event, date, shift)).filter(Boolean)
        .sort((a, b) => a.startMinute - b.startMinute || a.endMinute - b.endMinute
            || String(a.event.oldId ?? a.event.id).localeCompare(String(b.event.oldId ?? b.event.id)));
    const clusters = [];
    let cluster;
    clipped.forEach(segment => {
        if (!cluster || segment.startMinute >= cluster.end) {
            cluster = { end: segment.endMinute, segments: [] };
            clusters.push(cluster);
        }
        cluster.end = Math.max(cluster.end, segment.endMinute);
        cluster.segments.push(segment);
    });
    const segments = [];
    const overflow = [];
    clusters.forEach(group => {
        const laneEnds = [];
        const visible = [];
        group.segments.forEach(segment => {
            let lane = laneEnds.findIndex(end => end <= segment.startMinute);
            if (lane === -1) lane = laneEnds.length;
            if (lane >= 3) {
                overflow.push(segment);
            } else {
                laneEnds[lane] = segment.endMinute;
                visible.push({ ...segment, lane });
            }
        });
        visible.forEach(segment => segments.push({ ...segment, columns: laneEnds.length }));
    });
    return { segments, overflow };
}

function getBoardDayMetrics(events, date, shift = BOARD_SHIFT) {
    const totals = { manpower: 0, targetOutput: 0, pending: false };
    const sunday = moment(date).day() === 0;
    events.forEach(event => {
        if (sunday && event.ot !== true && getPlanningTargetOutput(event, date) === 0) return;
        if (!clipBoardEvent(event, date, shift)) return;
        const manpower = getPlanningManpower(event);
        totals.manpower = totals.manpower == null || manpower == null ? null : totals.manpower + manpower;
        totals.targetOutput += getPlanningTargetOutput(event, date);
        totals.pending = totals.pending || event.metricsPending === true;
    });
    return totals;
}

function subtractBoardWorkingHours(timestamp, hours, operationHours, ot = false, otSaturday = false) {
    const cursor = moment(timestamp);
    if (hours <= 0) return cursor.format(TIMESTAMP_FORMAT);
    const day = cursor.clone().startOf('day');
    let remaining = hours;
    // Reverse the existing Mon 07:00–Sat 07:00 working windows, not elapsed calendar time.
    for (let days = 0; days < 3640; days += 1, day.subtract(1, 'day')) {
        if ((day.day() === 0 && !ot) || (day.day() === 6 && !otSaturday)) continue;
        const window = getDayWorkWindow(day, operationHours);
        const end = moment.min(cursor, window.end);
        const available = Math.max(0, end.diff(window.start, 'hours', true));
        if (remaining <= available) return end.subtract(remaining, 'hours').format(TIMESTAMP_FORMAT);
        remaining -= available;
    }
    throw new Error('The job duration exceeds the supported planning range.');
}

function moveBoardEvent(event, targetTimestamp, sourceTimestamp = event.start) {
    const operationHours = Number(event.operationHours) || 24;
    const hours = countPlanningWorkingHours(event.start, event.end, operationHours, false, event.ot === true, event.otSaturday === true);
    const target = moment(targetTimestamp);
    const source = moment(sourceTimestamp);
    if (!target.isValid() || !source.isValid() || hours <= 0) throw new Error('The job has no valid working duration.');
    const anchor = formatPlanningStartDate(target, true, operationHours, event.ot === true, event.otSaturday === true);
    const workingOffset = countPlanningWorkingHours(event.start, source, operationHours, false, event.ot === true, event.otSaturday === true);
    const start = subtractBoardWorkingHours(anchor, workingOffset, operationHours, event.ot === true, event.otSaturday === true);
    return { start, end: formatPlanningEndDate(start, hours, true, operationHours, false, event.ot === true, event.otSaturday === true) };
}

function resizeBoardEvent(event, edge, timestamp) {
    if (!['start', 'end'].includes(edge) || !moment(timestamp).isValid()) throw new Error('Invalid resize time.');
    const operationHours = Number(event.operationHours) || 24;
    const start = formatPlanningStartDate(edge === 'start' ? timestamp : event.start, true, operationHours, event.ot === true, event.otSaturday === true);
    const end = edge === 'end' ? timestamp : event.end;
    if (!moment(end).isAfter(start)) throw new Error('The end must be after the start.');
    const hours = countPlanningWorkingHours(start, end, operationHours, false, event.ot === true, event.otSaturday === true);
    if (hours <= 0) throw new Error('The job must include working time.');
    return { start, end: formatPlanningEndDate(start, hours, true, operationHours, false, event.ot === true, event.otSaturday === true) };
}

module.exports = { BOARD_SHIFT, getBoardWindow, createBoardSlots, getBoardMachines, clipBoardEvent, layoutBoardDay, getBoardDayMetrics, moveBoardEvent, resizeBoardEvent };
